import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { LogoImage, PageTitle, PageSubtitle } from 'features/freeTrial';
import { useVerifyQuery } from 'services/freeTrial';
import {
  useFreeTrialContext,
  useFreeTrialPageNavigation,
} from 'providers/FreeTrialProvider';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import { useLocationSearchParam } from 'hooks/useLocationSearch';
import Box from '@mui/material/Box';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import EastIcon from '@mui/icons-material/East';
import Stack from '@mui/material/Stack';

export default function EmailVerifiedPage() {
  const { actions, meta } = useFreeTrialContext();
  const { announceSnackbar } = useSnackbarForMessage();
  const navigateToPage = useFreeTrialPageNavigation();

  // verifyToken is obtained from the URL:
  // freeTrial.html#/verify?verifyToken=5034b2bb-55eb-4190-98d5-6cea81db836e
  const verifyToken = useLocationSearchParam('verifyToken');

  const query = useVerifyQuery(
    { verifyToken },
    {
      skip: !verifyToken,
    }
  );

  // Listener for errors to redirect to the default error page
  useHandleErrorRedirection(query);

  // Once we make the verify call, it is safe to redirect to the Basic Info Page
  // We check if we have the correct data. This should never happen.
  // Then if all is good, we set the registration token and navigate to the basic info page
  const handleResumeSetup = async () => {
    // Check if we have the correct data. This should never happen.
    // But we check anyway to ensure we're not missing anything.
    if (!query?.data?.registrationToken && !query?.data?.companyName) {
      // If not, navigate to the error page
      console.log(`%cError: Verify Page`, 'color: red');
      console.error('Missing registrationToken and companyName');
      navigateToPage(meta.pages.ERROR_DEFAULT);
      announceSnackbar(
        'Verification failed. Please contact support for assistance.',
        'error'
      );
    } else {
      // Assign the registration token to the context
      // Set the Company Name in the context for Basic Info Page(s)
      // Navigate to the basic info page
      actions.setRegistrationToken(query.data.registrationToken);
      actions.setFormValues(
        { companyName: query.data.companyName },
        meta.pages.BASIC_INFO_FORM
      );
      navigateToPage(meta.pages.BASIC_INFO_FORM);
    }
  };

  return (
    <Content>
      <Stack direction="row" alignItems="center" gap={2}>
        <LogoImage />
        <PageTitle>Email Verified</PageTitle>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <Stack gap={2}>
        <PageSubtitle>
          You can return to the setup page and resume implementing your
          WorkforceHub account.
        </PageSubtitle>
        <PageSubtitle>
          If you've closed the original tab, you can resume the setup process by
          clicking the link below.
        </PageSubtitle>
      </Stack>
      <Stack sx={{ mt: 2 }} alignItems="center">
        <Button
          endIcon={<EastIcon sx={{ mb: '2px', ml: '4px' }} fontSize="small" />}
          disabled={!verifyToken || query.isLoading || query.isFetching} // We don't want them to resume until verify is complete see Notes below
          loadingPosition="end"
          variant="text"
          onClick={handleResumeSetup}
        >
          Resume Setup
        </Button>
      </Stack>
    </Content>
  );
}

// ------------------------------------------------------------- //
// Styles and components
// ------------------------------------------------------------- //
const Content = styled(Box)(({ theme }) => ({
  width: 'max-content',
  margin: `${theme.spacing(4)} auto`,
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0),
  },
  [theme.breakpoints.down('md')]: {
    width: 'auto',
  },
}));

// ------------------------------------------------------------- //
// Redirection
// ------------------------------------------------------------- //
function useHandleErrorRedirection(query) {
  const hasRedirected = useRef(false);
  const { isError, error } = query;
  const { meta } = useFreeTrialContext();
  const { announceSnackbar } = useSnackbarForMessage();
  const navigateToPage = useFreeTrialPageNavigation();
  const cleanup = () => {
    hasRedirected.current = false;
  };

  useEffect(() => {
    if (isError && !hasRedirected.current) {
      hasRedirected.current = true;
      console.log(`%cError: Verify Page`, 'color: red');
      console.error(error);
      navigateToPage(meta.pages.ERROR_DEFAULT);
      announceSnackbar(
        'Verification failed. Please contact support for assistance.',
        'error'
      );
    }
    return cleanup();
  }, [isError]);
}

// ------------------------------------------------------------- //
// Notes on the Verification Process
// ------------------------------------------------------------- //
// Implementation Overview:
// We're adopting an unconventional approach by using a query hook for a POST call.
// The Challenge:
// It's crucial to initiate the verification process immediately to transition from
// the "Check Your Email" page to the "Basic Info" page as soon as the user is verified.
// This involves polling the status to detect when the user verification is complete.
// Without initiating the verification on this page, users would remain on the
// "Check Your Email" page until they manually proceed by clicking the setup resume button.
// However, this approach does not align with the product requirements.

// The Solution:
// By leveraging a query for the immediate execution of the verification call, we avoid
// requiring user interaction for this step. If the query fails, the user is redirected
// to a default error page, managed by the useHandleErrorRedirection hook.
// Moreover, the query's isLoading/isFetching states prevent interaction with the setup
// resume button until the verification call is finalized. Additionally, the absence of a
// verifyToken automatically disables this button, ensuring a smoother user experience.
